import React, { useState, useEffect, useCallback } from 'react';
import { BreakfastPhotoUploader } from './breakfast/BreakfastPhotoUploader';
import { useDevice } from '../contexts/DeviceContext';
import { UploadFile, UploadSession, FileMetadata } from '../types';
import { SessionAPI } from '../utils/sessionApi';
import { StudioS3Uploader, UploadProgressDetails } from '../utils/cloudinaryUpload';
import { IOSSafariImageCompressor } from '../utils/imageCompression';
import { ExifExtractor } from '../utils/exifExtraction';

export interface FlowUploadHeader {
  title: string;
  subtitle?: string;
}

interface PhotoUploaderProps {
  sessionId?: string;
  sessionKey?: string;
  flowMode?: boolean;
  onFlowUploadComplete?: () => void;
  onFlowHeaderChange?: (header: FlowUploadHeader) => void;
}

const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  sessionId: sessionIdProp,
  sessionKey: sessionKeyProp,
  flowMode = false,
  onFlowUploadComplete,
  onFlowHeaderChange,
}) => {
  const { deviceInfo, uploadOptions, updateMemoryUsage } = useDevice();
  const hasInitialSessionCredentials = Boolean(
    flowMode &&
      sessionIdProp?.trim() &&
      sessionKeyProp?.trim(),
  );
  
  // Session state
  const [session, setSession] = useState<UploadSession>({
    id: sessionIdProp?.trim() || 'unknown',
    key: sessionKeyProp?.trim() || '',
    isValid: hasInitialSessionCredentials,
    status: hasInitialSessionCredentials ? 'ready' : 'validating',
    batchNumber: 1,
  });

  // Upload state
  const [uploads, setUploads] = useState<UploadFile[]>([]);
  const [isUploadingBatch, setIsUploadingBatch] = useState(false);
  const [isRetryingFailedBatch, setIsRetryingFailedBatch] = useState(false);
  const [pendingSelectionCount, setPendingSelectionCount] = useState(0);
  const uploadsRef = React.useRef<UploadFile[]>([]);
  const onDropRef = React.useRef<(files: File[]) => Promise<void>>(async () => {});
  const flowAdvanceTriggeredRef = React.useRef(false);
  const uploadBatchInProgressRef = React.useRef(false);
  
  // Metadata batching - queue metadata to send in batches
  const metadataQueueRef = React.useRef<any[]>([]);
  const METADATA_BATCH_SIZE = 8; // Send metadata in batches of 8
  
  const [compressionEnabled] = useState(deviceInfo.isIOSSafari);

  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const updateUploads = useCallback((updater: (current: UploadFile[]) => UploadFile[]) => {
    setUploads(current => {
      const next = updater(current);
      uploadsRef.current = next;
      return next;
    });
  }, []);

  // Initialize session from props or URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idParam = sessionIdProp ?? params.get('id');
    const keyParam = sessionKeyProp ?? params.get('key');

    const sessionId = idParam?.trim() || 'unknown';
    const sessionKey = keyParam?.trim() || '';

    console.log('🔍 Initializing session:', { sessionId, hasKey: !!sessionKey, flowMode });

    setSession(prev => ({
      ...prev,
      id: sessionId,
      key: sessionKey,
      isValid: flowMode && Boolean(sessionKey) ? true : prev.isValid,
      status: flowMode && Boolean(sessionKey) ? 'ready' : prev.status,
    }));

    validateSession(sessionId, sessionKey);
    loadBatchNumber(sessionId, sessionKey);
  }, [sessionIdProp, sessionKeyProp, flowMode]);

  // Prevent page refresh/close during uploads
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isCurrentlyUploading = isUploadingBatch || uploads.some(u => u.isUploading);
      const hasUploadedFiles = uploads.some(u => u.downloadUrl);
      
      if (isCurrentlyUploading || (hasUploadedFiles && !uploads.every(u => u.downloadUrl))) {
        const message = 'Upload in progress - leaving will lose your photos!';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploadingBatch, uploads]);

  const validateSession = async (sessionId: string, key: string) => {
    setSession(prev => ({ ...prev, status: 'validating' }));
    
    // Check for empty key first (like Flutter app does)
    if (!key || key.trim() === '') {
      console.log('❌ Session validation failed: empty key');
      setSession(prev => ({
        ...prev,
        isValid: false,
        status: 'error',
        prompt: 'Invalid Upload Link. Please contact support.',
      }));
      return;
    }
    
    try {
      // Test mode check
      if (SessionAPI.isTestMode(sessionId, key)) {
        const testResponse = SessionAPI.createTestSession();
        setSession(prev => ({
          ...prev,
          isValid: true,
          status: 'ready',
          prompt: testResponse.prompt,
          bookType: testResponse.bookType,
          minPhotos: testResponse.minPhotos,
          maxPhotos: testResponse.maxPhotos,
        }));
        return;
      }

      const response = await SessionAPI.validateSession(sessionId, key);
      
      setSession(prev => ({
        ...prev,
        isValid: response.valid,
        status: response.valid ? 'ready' : 'error',
        prompt: response.prompt || (response.valid ? 'Your upload session is ready' : 'Invalid Upload Link. Please contact support.'),
        bookType: response.bookType,
        minPhotos: response.minPhotos,
        maxPhotos: response.maxPhotos,
      }));
      
    } catch (error) {
      console.error('❌ Session validation failed:', error);
      setSession(prev => ({
        ...prev,
        isValid: false,
        status: 'error',
        prompt: 'Invalid Upload Link. Please contact support.',
      }));
    }
  };

  const loadBatchNumber = async (sessionId: string, key: string) => {
    try {
      const batchNumber = await SessionAPI.getNextBatchNumber(sessionId, key);
      setSession(prev => ({ ...prev, batchNumber }));
      console.log('✅ Batch number loaded:', batchNumber);
    } catch (error) {
      console.warn('⚠️ Failed to load batch number:', error);
    }
  };

  // Helper function to remove duplicate files
  const removeDuplicates = useCallback((allUploads: UploadFile[]): UploadFile[] => {
    const seen = new Set<string>();
    const unique: UploadFile[] = [];
    
    for (const upload of allUploads) {
      const key = `${upload.file.name}_${upload.file.size}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(upload);
      }
    }
    
    return unique;
  }, []);

  // Metadata batching functions
  const flushMetadataQueue = useCallback(async (force: boolean = false) => {
    const queue = metadataQueueRef.current;
    
    // Only flush if we have items and either force flush or reached batch size
    if (queue.length === 0 || (!force && queue.length < METADATA_BATCH_SIZE)) {
      return;
    }
    
    // Take items from queue
    const batchToSend = queue.splice(0, queue.length);
    
    console.log(`📦 Flushing metadata queue: ${batchToSend.length} items`);
    
    try {
      await SessionAPI.saveUploadMetadataBatch(batchToSend);
    } catch (error) {
      console.error('❌ Failed to flush metadata queue:', error);
    }
  }, [METADATA_BATCH_SIZE]);

  const queueMetadata = useCallback(async (metadataItem: any) => {
    // Add to queue
    metadataQueueRef.current.push(metadataItem);
    
    console.log(`📋 Queued metadata (${metadataQueueRef.current.length}/${METADATA_BATCH_SIZE}): ${metadataItem.filename}`);
    
    // Flush if we've reached batch size
    if (metadataQueueRef.current.length >= METADATA_BATCH_SIZE) {
      await flushMetadataQueue(false);
    }
  }, [METADATA_BATCH_SIZE, flushMetadataQueue]);

  const patchUpload = useCallback((uploadId: string, patch: Partial<UploadFile>) => {
    updateUploads((current) => current.map((upload) => (
      upload.id === uploadId ? { ...upload, ...patch } : upload
    )));
  }, [updateUploads]);

  // Fast sequential processing with optimizations
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!session.isValid) {
      console.warn('⚠️ Session not valid, cannot process files');
      return;
    }

    console.log(`📁 Processing ${acceptedFiles.length} files`);
    updateMemoryUsage();

    const batchTimestamp = Date.now();

    try {
      const stubs: UploadFile[] = acceptedFiles.map((file, index) => ({
        id: `upload_${batchTimestamp}_${index}`,
        file,
        isUploading: false,
        isPreparing: true,
        prepareProgress: 0,
        progress: 0,
        originalSize: file.size,
        compressedSize: file.size,
      }));

      // Add the whole batch at 0% first so the progress denominator is stable
      // before any single file can finish preparing or uploading.
      updateUploads((current) => removeDuplicates([...current, ...stubs]));

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        const uploadId = `upload_${batchTimestamp}_${i}`;

        console.log(`🔄 Processing file ${i + 1}/${acceptedFiles.length}: ${file.name}`);

        if (file.size > uploadOptions.maxFileSize) {
          console.warn(`⚠️ File ${file.name} exceeds size limit: ${file.size} > ${uploadOptions.maxFileSize}`);
        }

        let processedFile = file;
        let compressed = false;
        const originalSize = file.size;
        let compressedSize = file.size;

        patchUpload(uploadId, { prepareProgress: 0.15 });

        if (compressionEnabled && ExifExtractor.isImageFile(file)) {
          try {
            console.log(`🗜️ Compressing ${file.name}...`);
            const compressionResult = await IOSSafariImageCompressor.compressImage(
              file,
              deviceInfo.isIOSSafari
            );

            processedFile = compressionResult.compressedFile;
            compressed = compressionResult.compressionRatio > 1.1;
            compressedSize = compressionResult.compressedSize;

            console.log(`✅ Compression complete for ${file.name}: ${compressionResult.compressionRatio.toFixed(2)}x`);
          } catch (error) {
            console.warn(`⚠️ Compression failed for ${file.name}:`, error);
          }
        }

        patchUpload(uploadId, { prepareProgress: 0.6, file: processedFile, compressed, compressedSize });

        let metadata: FileMetadata = { hasCreationDate: false, hasLocation: false };
        if (ExifExtractor.isImageFile(processedFile)) {
          try {
            console.log(`🔍 Extracting EXIF from ${file.name}...`);
            metadata = await ExifExtractor.extractMetadata(processedFile, deviceInfo.isIOSSafari);
            console.log(`✅ EXIF extraction complete for ${file.name}:`, metadata);
          } catch (error) {
            console.warn(`⚠️ EXIF extraction failed for ${file.name}:`, error);
          }
        }

        patchUpload(uploadId, {
          file: processedFile,
          isPreparing: false,
          prepareProgress: 1,
          compressed,
          originalSize,
          compressedSize,
          metadata,
        });

        if (deviceInfo.isIOSSafari && i < acceptedFiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      updateMemoryUsage();
    } catch (error) {
      console.error('❌ File processing error:', error);
    }
  }, [
    session.isValid,
    compressionEnabled,
    deviceInfo.isIOSSafari,
    uploadOptions.maxFileSize,
    updateMemoryUsage,
    removeDuplicates,
    updateUploads,
    patchUpload,
  ]);

  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      const selectedFiles = files ? Array.from(files) : [];
      if (selectedFiles.length > 0) {
        console.log(`📁 Selected ${selectedFiles.length} files`);
        setPendingSelectionCount(selectedFiles.length);
        void onDropRef.current(selectedFiles).finally(() => {
          setPendingSelectionCount(0);
        });
      }
      event.target.value = '';
    },
    [],
  );

  const queueUploadMetadata = useCallback(async (
    response: any,
    uploadData: UploadFile,
  ) => {
    try {
      const metadataItem = {
        uploadId: uploadData.id,
        sessionId: session.id,
        key: session.key,
        filename: uploadData.file.name,
        originalFilename: uploadData.file.name,
        url: response.secure_url,
        s3Key: response.public_id,
        uploadSource: 'AWS',
        fileSize: uploadData.compressedSize || uploadData.file.size,
        isHeicOriginal: uploadData.file.name.toLowerCase().includes('.heic'),
        hasMetadata: uploadData.metadata?.hasCreationDate || uploadData.metadata?.hasLocation || false,
        hasCreationDate: uploadData.metadata?.hasCreationDate || false,
        hasLocation: uploadData.metadata?.hasLocation || false,
        createdAt: uploadData.metadata?.createdAt,
        latitude: uploadData.metadata?.latitude,
        longitude: uploadData.metadata?.longitude,
        batchNumber: session.batchNumber,
      };

      await queueMetadata(metadataItem);
    } catch (error) {
      console.error(`❌ Failed to queue metadata for ${uploadData.file.name}:`, error);
    }
  }, [queueMetadata, session.batchNumber, session.id, session.key]);

  const runUploadBatch = useCallback(async (
    batchUploads: UploadFile[],
    maxConcurrent: number,
    isRetry: boolean,
  ) => {
    await StudioS3Uploader.uploadBatch(
      batchUploads,
      session.id,
      session.key,
      maxConcurrent,
      {
        useChunkedUpload: uploadOptions.enableChunkedUpload,
        maxRetries: uploadOptions.retryAttempts,
        retryDelay: isRetry ? 2500 : 1000,
        onFileStart: (uploadId: string) => {
          updateUploads(prev => prev.map(u =>
            u.id === uploadId ? {
              ...u,
              isUploading: true,
              isRetrying: isRetry,
              error: undefined,
              progress: isRetry ? 0 : u.progress,
              uploadedBytes: 0,
              totalBytes: u.file.size,
              uploadSpeedBytesPerSecond: 0,
              uploadStartTime: Date.now(),
              retryCount: isRetry ? (u.retryCount || 0) + 1 : u.retryCount,
            } : u
          ));
        },
        onFileProgress: (
          uploadId: string,
          progress: number,
          details?: UploadProgressDetails,
        ) => {
          updateUploads(prev => prev.map(u =>
            u.id === uploadId ? {
              ...u,
              progress: Math.max(u.progress, progress),
              uploadedBytes: Math.max(
                u.uploadedBytes || 0,
                details?.loadedBytes ?? Math.round(progress * (u.totalBytes || u.file.size)),
              ),
              totalBytes: details?.totalBytes ?? u.totalBytes ?? u.file.size,
              uploadSpeedBytesPerSecond: details?.speedBytesPerSecond ?? u.uploadSpeedBytesPerSecond,
            } : u
          ));
        },
        onFileComplete: async (uploadId: string, response: any, uploadData: UploadFile) => {
          updateUploads(prev => prev.map(u =>
            u.id === uploadId ? {
              ...u,
              downloadUrl: response.secure_url,
              isUploading: false,
              isRetrying: false,
              progress: 1,
              uploadedBytes: u.totalBytes || uploadData.file.size,
              totalBytes: u.totalBytes || uploadData.file.size,
              uploadSpeedBytesPerSecond: 0,
              uploadEndTime: Date.now(),
              error: undefined,
            } : u
          ));

          await queueUploadMetadata(response, uploadData);
        },
        onFileError: (uploadId: string, error: string) => {
          updateUploads(prev => prev.map(u =>
            u.id === uploadId
              ? {
                  ...u,
                  error,
                  isUploading: false,
                  isRetrying: false,
                  progress: 0,
                  uploadSpeedBytesPerSecond: 0,
                }
              : u
          ));
        }
      }
    );
  }, [
    queueUploadMetadata,
    session.id,
    session.key,
    uploadOptions.enableChunkedUpload,
    uploadOptions.retryAttempts,
    updateUploads,
  ]);

  const advanceFlowAfterUpload = useCallback(() => {
    if (!flowMode || !onFlowUploadComplete || flowAdvanceTriggeredRef.current) {
      return;
    }

    flowAdvanceTriggeredRef.current = true;
    onFlowUploadComplete();
  }, [flowMode, onFlowUploadComplete]);

  const updateFinalSessionStatus = useCallback(async () => {
    const currentUploads = uploadsRef.current;
    const successful = currentUploads.filter(u => u.downloadUrl).length;
    const failed = currentUploads.filter(u => u.error && !u.downloadUrl).length;

    if (failed === 0 && successful > 0) {
      console.log(`📝 Updating session status: uploaded`);
      await SessionAPI.updateSessionStatus(session.id, session.key, 'uploaded');
      setSession(prev => ({ ...prev, status: 'uploaded' }));
      console.log(`✅ All ${successful} uploads completed successfully`);
      advanceFlowAfterUpload();
    } else {
      console.warn(`⚠️ Upload completed with ${failed} failures out of ${currentUploads.length} files`);
    }
  }, [advanceFlowAfterUpload, session.id, session.key]);

  const retryFailedUploads = useCallback(async (autoRetry: boolean = false) => {
    const failedUploads = uploadsRef.current.filter(u => u.error && !u.downloadUrl);
    if (!session.isValid || failedUploads.length === 0) return;

    const retryConcurrency = Math.max(1, Math.min(2, uploadOptions.maxConcurrentUploads));
    console.log(`🔄 Retrying ${failedUploads.length} failed uploads (${retryConcurrency} at a time)`);

    setIsRetryingFailedBatch(true);
    try {
      await runUploadBatch(failedUploads, retryConcurrency, true);
      await flushMetadataQueue(true);
      await updateFinalSessionStatus();
    } catch (error) {
      console.error(autoRetry ? '❌ Automatic retry pass failed:' : '❌ Retry failed:', error);
      await flushMetadataQueue(true);
    } finally {
      setIsRetryingFailedBatch(false);
    }
  }, [
    flushMetadataQueue,
    runUploadBatch,
    session.isValid,
    updateFinalSessionStatus,
    uploadOptions.maxConcurrentUploads,
  ]);

  const startUpload = useCallback(async () => {
    if (uploadBatchInProgressRef.current) {
      return;
    }

    const currentUploads = uploadsRef.current;
    if (!session.isValid || currentUploads.length === 0) return;

    const pendingUploads = currentUploads.filter(
      (upload) => !upload.downloadUrl &&
        !upload.error &&
        !upload.isUploading &&
        !upload.isPreparing,
    );
    if (pendingUploads.length === 0) return;

    uploadBatchInProgressRef.current = true;
    console.log(`☁️ Starting upload of ${pendingUploads.length} files`);

    try {
      setIsUploadingBatch(true);
      await SessionAPI.updateSessionStatus(session.id, session.key, 'uploading');
      setSession(prev => ({ ...prev, status: 'uploading' }));

      await runUploadBatch(pendingUploads, uploadOptions.maxConcurrentUploads, false);

      console.log('🔄 Flushing remaining metadata queue...');
      await flushMetadataQueue(true);
      console.log('✅ Metadata queue flushed');

      const failedAfterFirstPass = uploadsRef.current.filter(u => u.error && !u.downloadUrl);
      if (failedAfterFirstPass.length > 0) {
        console.warn(`⚠️ ${failedAfterFirstPass.length} uploads failed; starting one automatic retry pass`);
        await retryFailedUploads(true);
      } else {
        await updateFinalSessionStatus();
      }
    } catch (error) {
      console.error('❌ Upload process error:', error);
      await flushMetadataQueue(true);
    } finally {
      uploadBatchInProgressRef.current = false;
      setIsUploadingBatch(false);
    }
  }, [
    flushMetadataQueue,
    retryFailedUploads,
    runUploadBatch,
    session.id,
    session.isValid,
    session.key,
    updateFinalSessionStatus,
    uploadOptions.maxConcurrentUploads,
  ]);

  const preparingCount = uploads.filter((upload) => upload.isPreparing).length;

  // In the web flow, start uploading as soon as photos are ready — no extra tap.
  useEffect(() => {
    if (!flowMode || !session.isValid || preparingCount > 0 || uploadBatchInProgressRef.current) {
      return;
    }

    const currentUploads = uploadsRef.current;
    const hasPending = currentUploads.some(
      (upload) => !upload.downloadUrl &&
        !upload.error &&
        !upload.isUploading &&
        !upload.isPreparing,
    );
    if (!hasPending) {
      return;
    }

    void startUpload();
  }, [flowMode, session.isValid, preparingCount, startUpload]);

  const clearAll = () => {
    uploadBatchInProgressRef.current = false;
    updateUploads(() => []);
    updateMemoryUsage();
    console.log('🧹 Cleared all uploads');
  };

  const openGooglePhotosImporter = () => {
    console.warn('⚠️ Google Photos import is not enabled yet');
  };

  const completeSession = async () => {
    try {
      const numPhotos = uploads.filter(u => u.downloadUrl).length;

      if (flowMode && onFlowUploadComplete) {
        await SessionAPI.updateSessionStatus(session.id, session.key, 'uploaded');
        setSession(prev => ({ ...prev, status: 'uploaded' }));
        onFlowUploadComplete();
        return;
      }

      await SessionAPI.markSessionComplete(session.id, session.key, numPhotos);
      setSession(prev => ({ ...prev, status: 'complete' }));
      console.log('✅ Session completed with', numPhotos, 'photos');
    } catch (error) {
      console.error('❌ Failed to complete session:', error);
    }
  };

  const openFilePicker = () => {
    if (!session.isValid) {
      console.warn('⚠️ Session not valid, cannot open file picker');
    }
  };

  return (
    <>
      <BreakfastPhotoUploader
        session={session}
        uploads={uploads}
        isRetryingFailedBatch={isRetryingFailedBatch}
        pendingSelectionCount={pendingSelectionCount}
        flowMode={flowMode}
        onFlowHeaderChange={onFlowHeaderChange}
        onFileSelect={openFilePicker}
        onFileInputChange={handleFileInputChange}
        onGooglePhotosSelect={openGooglePhotosImporter}
        onStartUpload={startUpload}
        onRetryFailed={() => retryFailedUploads(false)}
        onClearAll={clearAll}
        onMakeMyBook={completeSession}
      />
      
      {/* TODO: Re-enable after Google Cloud OAuth verification complete
      <GooglePhotosImporter
        isOpen={showGooglePhotosImporter}
        onClose={() => setShowGooglePhotosImporter(false)}
        onPhotosSelected={handleGooglePhotosImport}
        maxPhotos={session.maxPhotos}
      />
      */}
    </>
  );
};

export default PhotoUploader;
